import type { HRDepartment, HREmployee } from '@cloud/db'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ModuleGuard } from '@/components/module-guard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

const departmentSchema = z.object({
  name: z.string().min(1, 'Department name is required'),
  description: z.string().optional(),
  managerId: z.number().optional(),
})

type DepartmentFormValues = z.infer<typeof departmentSchema>

export const Route = createFileRoute('/hr/departments')({
  component: DepartmentsPage,
})

function DepartmentsPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <DepartmentsList />
    </ModuleGuard>
  )
}

function DepartmentsList() {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingDepartment, setEditingDepartment] = useState<HRDepartment | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; departmentId: number | null }>({
    open: false,
    departmentId: null,
  })

  const {
    data: departmentsData,
    isLoading,
    refetch,
  } = useQuery(
    orpc.hr.getDepartments.queryOptions({
      input: {},
    }),
  )
  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({
      input: { page: 1, limit: 100 },
    }),
  )

  const departments = (departmentsData ?? []) as (HRDepartment & { employeeCount: number })[]
  const employees = (employeesData?.data ?? []) as HREmployee[]

  const createMutation = useMutation(
    orpc.hr.createDepartment.mutationOptions({
      onSuccess: () => {
        toast.success('Department created')
        refetch()
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateMutation = useMutation(
    orpc.hr.updateDepartment.mutationOptions({
      onSuccess: () => {
        toast.success('Department updated')
        refetch()
        setIsFormOpen(false)
        setEditingDepartment(null)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.hr.deleteDepartment.mutationOptions({
      onSuccess: () => {
        toast.success('Department deleted')
        refetch()
        setDeleteDialog({ open: false, departmentId: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const getManagerName = (managerId: number | null) => {
    if (!managerId) return '-'
    const emp = employees.find((e) => e.id === managerId)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${managerId}`
  }

  const handleEdit = (department: HRDepartment) => {
    setEditingDepartment(department)
    setIsFormOpen(true)
  }

  const handleDelete = async () => {
    if (deleteDialog.departmentId) {
      await deleteMutation.mutateAsync({ id: deleteDialog.departmentId })
    }
  }

  const handleFormSubmit = async (data: DepartmentFormValues) => {
    if (editingDepartment) {
      await updateMutation.mutateAsync({ id: editingDepartment.id, ...data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Departments</h1>
          <p className="text-muted-foreground">Manage organizational departments</p>
        </div>
        <Button
          onClick={() => {
            setEditingDepartment(null)
            setIsFormOpen(true)
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Department
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : departments.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No departments found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Department Name</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead>Employees</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.map((department) => (
                  <TableRow key={department.id}>
                    <TableCell className="font-medium">{department.name}</TableCell>
                    <TableCell>{getManagerName(department.managerId)}</TableCell>
                    <TableCell>{department.employeeCount}</TableCell>
                    <TableCell className="max-w-xs truncate">
                      {department.description ?? '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(department)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() =>
                            setDeleteDialog({ open: true, departmentId: department.id })
                          }
                          disabled={department.employeeCount > 0}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <DepartmentFormDialog
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open)
          if (!open) setEditingDepartment(null)
        }}
        department={editingDepartment}
        employees={employees}
        onSubmit={handleFormSubmit}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, departmentId: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Department</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this department? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface DepartmentFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  department: HRDepartment | null
  employees: HREmployee[]
  onSubmit: (data: DepartmentFormValues) => Promise<void>
}

function DepartmentFormDialog({
  open,
  onOpenChange,
  department,
  employees,
  onSubmit,
}: DepartmentFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<DepartmentFormValues>({
    resolver: zodResolver(departmentSchema),
    defaultValues: {
      name: '',
      description: '',
      managerId: undefined,
    },
  })

  // Reset form values when the department prop changes (switching
  // between create and edit mode, or editing a different department).
  useEffect(() => {
    if (open) {
      form.reset({
        name: department?.name ?? '',
        description: department?.description ?? '',
        managerId: department?.managerId ?? undefined,
      })
    }
  }, [open, department, form])

  const handleSubmit = async (data: DepartmentFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{department ? 'Edit Department' : 'Add Department'}</DialogTitle>
          <DialogDescription>
            {department ? 'Edit department details' : 'Create a new department'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Department Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Engineering" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Department description..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="managerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Manager</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === 'none' ? undefined : Number(value))
                    }
                    value={field.value?.toString() ?? 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select manager" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No manager</SelectItem>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? 'Saving...'
                  : department
                    ? 'Update Department'
                    : 'Create Department'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
